import { type FC, type ReactNode } from 'react';

import { cn } from '@repo/utils/common';

import { type HeroImage } from '@/shared/configs/hero.config';

interface HeroBackgroundProps {
  image: HeroImage;
  alt: string;
  children?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

/**
 * Full-viewport hero shell: a responsive background photo under a 45% black
 * overlay, with `children` centered on top. The container shows a solid dark
 * tone until the photo paints.
 */
export const HeroBackground: FC<HeroBackgroundProps> = ({
  image,
  alt,
  children,
  className,
  'data-testid': testId,
}) => (
  <div
    className={cn(
      // Fill the viewport below the fixed toolbar. Heights track
      // COMBINED_BAR_HEIGHT (100/108/128px) so hero + content offset == 100vh.
      'relative flex h-[calc(100vh-100px)] w-full flex-col sm:h-[calc(100vh-108px)] md:h-[calc(100vh-128px)]',
      'bg-neutral-900',
      'after:absolute after:inset-0 after:z-10 after:bg-black/45 after:bg-blend-multiply after:content-[""]',
      className,
    )}
    data-testid={testId}
  >
    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-8">
      {children}
    </div>
    <picture className="contents">
      {image.portrait && (
        <source
          media={image.portrait.media}
          srcSet={image.portrait.srcSet}
          sizes={image.portrait.sizes}
        />
      )}
      <img
        className="size-full flex-1 object-cover"
        // srcSet/sizes before src so a client render never starts the fallback fetch first.
        srcSet={image.landscape.srcSet}
        sizes={image.landscape.sizes}
        src={image.landscape.src}
        fetchPriority="high"
        alt={alt}
      />
    </picture>
  </div>
);
